import BaseService from './BaseService';
import { AnomalyReport, CONTEXT_DOMAIN, ContextEntry, ContextInput, ContextPatch, DayCurves, OverviewReport, PeriodSpec, SubjectOption, Thresholds, periodKey } from 'pages/Baselines/types';

function rethrow(err: any): never {
  throw new Error(err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'request failed');
}

// /baselines/* — the collector's baselines service (src/intelligence/baselines).
class BaselinesService extends BaseService {
  async listSubjects(store: string): Promise<SubjectOption[]> {
    await this._getAccessToken();
    const res = await this.http.get('baselines/subjects', { params: { store } }).catch(rethrow);
    return Array.isArray(res.data?.subjects) ? res.data.subjects : [];
  }

  async anomaly(params: { subject: string; period: PeriodSpec; peers?: string; methods?: string; thresholds?: Thresholds; historyDays?: number; refresh?: boolean }): Promise<AnomalyReport> {
    await this._getAccessToken();
    const q: Record<string, string> = { subject: params.subject, period: periodKey(params.period) };
    if (params.historyDays) q.history = String(params.historyDays);
    if (params.thresholds) { q.near = String(params.thresholds.near); q.out = String(params.thresholds.out); }
    if (params.peers) q.peers = params.peers;
    if (params.methods) q.methods = params.methods;
    if (params.refresh) q.refresh = '1';
    const res = await this.http.get('baselines/anomaly', { params: q }).catch(rethrow);
    return res.data;
  }

  /** Drops every cached read on the API (store bundles and subject reports). */
  async refreshAll(): Promise<void> {
    await this._getAccessToken();
    await this.http.post('baselines/refresh').catch(rethrow);
  }

  /** Ad channels in aggregate, the store, and Google conversion actions: complete days ending yesterday. */
  async pipelines(params: { store: string; days: number; thresholds?: Thresholds; historyDays?: number; refresh?: boolean }): Promise<OverviewReport> {
    await this._getAccessToken();
    const res = await this.http.get('baselines/pipelines', { params: pipelineParams(params) }).catch(rethrow);
    return res.data;
  }

  async pipelineSubject(params: { store: string; subject: string; days: number; thresholds?: Thresholds; historyDays?: number; refresh?: boolean }): Promise<AnomalyReport> {
    await this._getAccessToken();
    const res = await this.http.get('baselines/pipelines/subject', { params: { ...pipelineParams(params), subject: params.subject } }).catch(rethrow);
    return res.data;
  }

  async overview(params: { store: string; period: PeriodSpec; thresholds?: Thresholds; historyDays?: number; refresh?: boolean }): Promise<OverviewReport> {
    await this._getAccessToken();
    const q: Record<string, string> = { store: params.store, period: periodKey(params.period) };
    if (params.refresh) q.refresh = '1';
    if (params.historyDays) q.history = String(params.historyDays);
    if (params.thresholds) { q.near = String(params.thresholds.near); q.out = String(params.thresholds.out); }
    const res = await this.http.get('baselines/overview', { params: q }).catch(rethrow);
    return res.data;
  }

  /** The shared context for baselines: active entries, and with `recent` the recently expired or retired too. */
  async listContext(recent: boolean): Promise<ContextEntry[]> {
    await this._getAccessToken();
    const res = await this.http.get('findings/context', { params: { domain: CONTEXT_DOMAIN, ...(recent ? { include: 'recent' } : {}) } }).catch(rethrow);
    if (!Array.isArray(res.data?.entries)) throw new Error('findings/context answered without entries');
    return res.data.entries;
  }

  /** The store is production Firebase, so writes are refused (409) outside production, as is a 41st cause entry. */
  async addContext(entry: ContextInput): Promise<ContextEntry> {
    await this._getAccessToken();
    const res = await this.http.post('findings/context', entry).catch(rethrow);
    return res.data;
  }

  async patchContext(id: string, patch: ContextPatch): Promise<ContextEntry> {
    await this._getAccessToken();
    const res = await this.http.patch(`findings/context/${encodeURIComponent(id)}`, patch).catch(rethrow);
    return res.data;
  }

  async curves(store: string): Promise<DayCurves> {
    await this._getAccessToken();
    const res = await this.http.get('baselines/curves', { params: { store } }).catch(rethrow);
    return res.data;
  }
}

export default BaselinesService;

function pipelineParams(params: { store: string; days: number; thresholds?: Thresholds; historyDays?: number; refresh?: boolean }): Record<string, string> {
  const q: Record<string, string> = { store: params.store, days: String(params.days) };
  if (params.refresh) q.refresh = '1';
  if (params.historyDays) q.history = String(params.historyDays);
  if (params.thresholds) { q.near = String(params.thresholds.near); q.out = String(params.thresholds.out); }
  return q;
}
